import React from "react";
import {Box, Button, Flex, Text} from "@chakra-ui/react";
import {UploadItem, UploadStatus} from "./types";

type Props = {
    uploads: UploadItem[];
    uploadPanelOpen: boolean;
    setUploadPanelOpen: (value: boolean | ((previous: boolean) => boolean)) => void;
    onClearDone: () => void;
    onCancelUpload: (id: string) => void;
    onCancelAll: () => void;
    onCloseIdle: () => void;
};

const ACTIVE_STATUSES: UploadStatus[] = ["queued", "uploading"];

const isActiveStatus = (status: UploadStatus) => ACTIVE_STATUSES.includes(status);

const getStatusLabel = (item: UploadItem) => {
    if (item.status === "queued") return "Queued";
    if (item.status === "uploading") return `Uploading ${item.progress || 0}%`;
    if (item.status === "cancelled") return "Cancelled";
    if (item.status === "done") return "Done";
    if (item.status === "error") return item.error || "Failed";
    return item.status;
};

const getDotColor = (status: UploadStatus) => {
    if (status === "done") return "#22c55e";
    if (status === "error") return "#ef4444";
    if (status === "cancelled") return "#94a3b8";
    return "#38bdf8";
};

const UploadQueuePanel = ({
    uploads,
    uploadPanelOpen,
    setUploadPanelOpen,
    onClearDone,
    onCancelUpload,
    onCancelAll,
    onCloseIdle
}: Props) => {
    if (uploads.length === 0) {
        return null;
    }

    const counts = uploads.reduce((acc, item) => {
        if (isActiveStatus(item.status)) {
            acc.active += 1;
        } else {
            acc.completed += 1;
        }
        return acc;
    }, {active: 0, completed: 0});

    return (
        <Box
            position={"fixed"}
            right={"24px"}
            bottom={"24px"}
            width={"min(360px, 92vw)"}
            bg={"#0f172a"}
            color={"#f8fafc"}
            borderRadius={"16px"}
            boxShadow={"0 24px 80px rgba(2, 6, 23, 0.6)"}
            zIndex={1500}
            overflow={"hidden"}
        >
            <Box
                display={"grid"}
                rowGap={"2px"}
                px={"16px"}
                py={"12px"}
                borderBottomWidth={uploadPanelOpen ? "1px" : "0px"}
                borderColor={"rgba(148, 163, 184, 0.2)"}
            >
                <Flex align={"center"} justify={"space-between"} gap={4}>
                    <Text fontWeight={600}>Uploads</Text>
                    <Flex align={"center"} gap={3}>
                        {counts.active > 0 ? (
                            <Button variant={"link"} color={"#f8fafc"} fontWeight={600} onClick={onCancelAll} minW={0} height={"auto"}>
                                Cancel all
                            </Button>
                        ) : null}
                        <Button variant={"link"} color={"#38bdf8"} fontWeight={600} onClick={onClearDone} minW={0} height={"auto"}>
                            Clear done
                        </Button>
                        <Button
                            variant={"link"}
                            color={"#38bdf8"}
                            fontWeight={600}
                            onClick={() => {
                                if (counts.active === 0) {
                                    onCloseIdle();
                                    return;
                                }
                                setUploadPanelOpen((previous) => !previous);
                            }}
                            minW={0}
                            height={"auto"}
                        >
                            {uploadPanelOpen ? "Hide" : "Show"}
                        </Button>
                    </Flex>
                </Flex>
                {counts.active > 0 || counts.completed > 0 ? (
                    <Text fontSize={"12px"} color={"#cbd5f5"}>
                        {counts.active} active · {counts.completed} done
                    </Text>
                ) : null}
            </Box>

            {uploadPanelOpen ? (
                <Box maxH={"260px"} overflowY={"auto"} px={"16px"} pt={"12px"} pb={"16px"} display={"grid"} rowGap={"10px"}>
                    {uploads.map((item) => (
                        <Flex key={item.id} align={"flex-start"} gap={"10px"}>
                            <Box width={"10px"} height={"10px"} borderRadius={"full"} bg={getDotColor(item.status)} mt={"6px"} flexShrink={0}/>
                            <Box flex={1} minW={0} display={"grid"} rowGap={"2px"}>
                                <Flex align={"center"} justify={"space-between"} gap={3}>
                                    <Text fontWeight={600} fontSize={"15px"} noOfLines={1}>{item.name}</Text>
                                    {isActiveStatus(item.status) ? (
                                        <Button
                                            variant={"link"}
                                            color={"#f8fafc"}
                                            fontWeight={600}
                                            minW={0}
                                            height={"auto"}
                                            onClick={() => onCancelUpload(item.id)}
                                        >
                                            Cancel
                                        </Button>
                                    ) : null}
                                </Flex>
                                <Text fontSize={"13px"} color={"#cbd5f5"}>
                                    {getStatusLabel(item)}
                                </Text>
                                {item.status === "uploading" ? (
                                    <Box height={"6px"} borderRadius={"999px"} bg={"rgba(148, 163, 184, 0.2)"} overflow={"hidden"}>
                                        <Box
                                            height={"100%"}
                                            borderRadius={"999px"}
                                            bg={"linear-gradient(90deg, #38bdf8, #22d3ee)"}
                                            width={`${item.progress || 0}%`}
                                            transition={"width 120ms ease"}
                                        />
                                    </Box>
                                ) : null}
                            </Box>
                        </Flex>
                    ))}
                </Box>
            ) : null}
        </Box>
    );
};

export default UploadQueuePanel;
